/* eslint-disable @typescript-eslint/await-thenable */
import Button from '@base/components/src/Button';
import FormSelect from '@base/components/src/FormSelect';
import Image from '@base/components/src/Image';
import { RedeemLoyaltyPoints } from '@package/core/src/bullseyeBucks/types';
import { required } from '@package/utils/src/formValidation';
import clsx from 'clsx';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { MouseEventHandler, useEffect, useState } from 'react';
import config from 'config-base';
import { BullseyeBucksFormProps } from './types';
import AnchorDialog from '@base/components/src/AnchorDialog';
import { apiClient } from '../../../../services';
import AlertsApiService from '@package/core/src/alerts/AlertsApiService';
import AlertsInteractor from '@package/core/src/alerts/AlertsInteractor';
import useLabelVatiants from '@package/hooks/src/useLabelVatiants';
import styles from './BullseyeBucksForm.module.css';
import GiftCardList from '../../../GiftCardList';

const alertsService = new AlertsApiService(apiClient);
const alertsInteractor = new AlertsInteractor(alertsService);

const InitialValues = {
  redemptionOption: 0,
};

const BullseyeBucksForm = ({
  id,
  fullWidth = false,
  buttonLabel,
  points,
  giftCards,
  redeemLoyaltyPoints,
  onDelete,
  classes,
}: BullseyeBucksFormProps) => {
  const [currentPoints, setCurrentPoints] = useState<number>(0);
  const [bbMessage, setBbMessage] = useState<string>('');
  const { v } = useLabelVatiants();

  const handleSubmit = async (values: RedeemLoyaltyPoints, formikHelpers: FormikHelpers<RedeemLoyaltyPoints>) => {
    try {
      await redeemLoyaltyPoints(values);
      formikHelpers.setValues(InitialValues);
      formikHelpers.setFieldValue('redemptionOption', '0');
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleChange(0);
  }, [points]);

  const handleChange = (value: number) => {
    if (points && points.redemptionAmounts.length > 0 && points.redemptionAmounts[value]) {
      setCurrentPoints(points ? points.redemptionAmounts[value] : 0);
    }
  };

  return (
    <div className={clsx(styles.bbFormContainer, classes)}>
      <p data-testid="bb-info" className={styles.bbBoldText}>
        <span className={clsx(styles.bbTitle, { [styles.bbTitleAGD]: config.brandCode !== '0' })}>
          {v('rewardPointsTitle')}
        </span>
        <AnchorDialog
          contentClassName={styles.serviceModal}
          dialogHeaderClassName={styles.serviceRadioDialogHeader}
          anchorContent={(open: MouseEventHandler<HTMLSpanElement>) => (
            <span
              onClick={async (e) => {
                const { data } = await alertsInteractor.getBullseyeBucksAlert();
                if (data) setBbMessage(data);
                open && open(e);
              }}
              data-testid="bb-info-icon"
              className={styles.infoIconWrapper}
              id={'bb-info-icon'}
            >
              <Image
                src="/marketplace-static/images/icons/infoIcon.svg"
                alt="info icon"
                width={14}
                height={14}
                className={`${styles.infoIcon} infoIcon`}
              />
            </span>
          )}
        >
          {(_: any, close: () => void) => (
            <div className={styles.clearIconContainer}>
              <div className={styles.messageContent} dangerouslySetInnerHTML={{ __html: bbMessage }} />
            </div>
          )}
        </AnchorDialog>
      </p>

      {points && points.redemptionAmounts.length > 0 ? (
        <Formik<RedeemLoyaltyPoints> initialValues={InitialValues} onSubmit={handleSubmit}>
          {({ handleSubmit, values }) => (
            <Form
              id={id}
              className={clsx(styles.bonusForm, {
                [styles.bonusFormFullWidth]: fullWidth,
              })}
              onSubmit={handleSubmit}
            >
              <div className={styles.pointsContainer}>
                <Field
                  id="bullseye-bucks-input"
                  data-testid={'bullseye-bucks-input'}
                  name="redemptionOption"
                  variant="rounded"
                  className={clsx(styles.points, { [styles.agdPoints]: config.brandCode !== '0' })}
                  options={points?.redemptionAmounts.map((point: number, idx: number) => ({
                    label: `$${point.toString()}`,
                    value: idx,
                  }))}
                  required
                  value={values.redemptionOption}
                  validate={required}
                  disableAutoComplete={true}
                  onChangeCallback={handleChange}
                  component={FormSelect}
                />
                <span className={styles.text}>{`${currentPoints * 100} of ${points?.earnedPoints} available`}</span>
              </div>
              <Button
                id={`${id}-apply-btn`}
                data-testid={`${id}-apply-btn`}
                fullWidth
                type="submit"
                size={'large'}
                variant={'outlined'}
                label={buttonLabel}
                classes={styles.giftCardApplyButton}
              />
            </Form>
          )}
        </Formik>
      ) : (
        <></>
      )}

      <GiftCardList
        id="bb-gift-card-list"
        title={v('bullseyeBucksApplied')}
        data={giftCards}
        onDelete={(serialNumber: string) => onDelete({ serialNumber })}
      />
    </div>
  );
};

export default BullseyeBucksForm;
